import React, { useState, useRef, useEffect, useCallback } from 'react'

interface TableInsertModalProps {
  show: boolean
  onClose: () => void
  onInsert: (rows: number, cols: number) => void
  theme?: string
  editorContainerRef?: React.RefObject<HTMLDivElement | null>
}

const MAX_ROWS = 20
const MAX_COLS = 10
const GRID_ROWS = 8
const GRID_COLS = 8

const TableInsertModal: React.FC<TableInsertModalProps> = ({ show, onClose, onInsert, theme, editorContainerRef }) => {
  const [rows, setRows] = useState(3)
  const [cols, setCols] = useState(3)
  const [hoverRow, setHoverRow] = useState(0)
  const [hoverCol, setHoverCol] = useState(0)
  const [useCustom, setUseCustom] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!show) {
      setHoverRow(0)
      setHoverCol(0)
      setUseCustom(false)
      setRows(3)
      setCols(3)
      setPosition(null)
      return
    }

    // Position the modal below the table toolbar button
    const container = editorContainerRef?.current
    const btn = container?.querySelector('.ql-customButtonTable') as HTMLElement | null
    if (btn && container) {
      const btnRect = btn.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()
      setPosition({
        top: btnRect.bottom - containerRect.top + 4,
        left: btnRect.left - containerRect.left,
      })
    }
  }, [show, editorContainerRef])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        const tableBtn = document.querySelector('.ql-customButtonTable')
        if (tableBtn && tableBtn.contains(e.target as Node)) return
        onClose()
      }
    }
    if (show) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [show, onClose])

  const handleGridClick = useCallback(() => {
    if (hoverRow > 0 && hoverCol > 0) {
      onInsert(hoverRow, hoverCol)
      onClose()
    }
  }, [hoverRow, hoverCol, onInsert, onClose])

  const handleCustomInsert = useCallback(() => {
    if (rows > 0 && cols > 0 && rows <= MAX_ROWS && cols <= MAX_COLS) {
      onInsert(rows, cols)
      onClose()
    }
  }, [rows, cols, onInsert, onClose])

  if (!show) return null

  const isDark = theme === 'dark'

  return (
    <div
      ref={modalRef}
      className={`table-insert-modal ${isDark ? 'dark' : ''}`}
      style={position ? { top: position.top, left: position.left } : undefined}
    >
      {!useCustom ? (
        <>
          <div className="table-insert-modal__label">
            {hoverRow > 0 && hoverCol > 0
              ? `${hoverRow} × ${hoverCol} Table`
              : 'Select table size'}
          </div>
          <div className="table-insert-modal__grid">
            {Array.from({ length: GRID_ROWS }, (_, r) => (
              <div key={r} className="table-insert-modal__grid-row">
                {Array.from({ length: GRID_COLS }, (_, c) => (
                  <div
                    key={c}
                    className={`table-insert-modal__grid-cell ${
                      r < hoverRow && c < hoverCol ? 'active' : ''
                    }`}
                    onMouseEnter={() => {
                      setHoverRow(r + 1)
                      setHoverCol(c + 1)
                    }}
                    onClick={handleGridClick}
                  />
                ))}
              </div>
            ))}
          </div>
          <button
            type="button"
            className="table-insert-modal__custom-btn"
            onClick={() => setUseCustom(true)}
          >
            Custom size...
          </button>
        </>
      ) : (
        <>
          <div className="table-insert-modal__label">Custom Table Size</div>
          <div className="table-insert-modal__custom-inputs">
            <label>
              Rows
              <input
                type="number"
                min={1}
                max={MAX_ROWS}
                value={rows}
                onChange={(e) => setRows(Math.min(MAX_ROWS, Math.max(1, Number(e.target.value))))}
              />
            </label>
            <span className="table-insert-modal__times">×</span>
            <label>
              Columns
              <input
                type="number"
                min={1}
                max={MAX_COLS}
                value={cols}
                onChange={(e) => setCols(Math.min(MAX_COLS, Math.max(1, Number(e.target.value))))}
              />
            </label>
          </div>
          <div className="table-insert-modal__actions">
            <button
              type="button"
              className="table-insert-modal__back-btn"
              onClick={() => setUseCustom(false)}
            >
              Back
            </button>
            <button
              type="button"
              className="table-insert-modal__insert-btn"
              onClick={handleCustomInsert}
            >
              Insert Table
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default TableInsertModal
